import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import Prismic from '@prismicio/client';
import _ from 'lodash';
import { RichText, RichTextBlock } from 'prismic-reactjs';
import { useFormik } from 'formik';
import { Client } from '../prismic-configuration';
import { PrismicJobListings } from '../types/prismic';
import useScrollPosition from '../helpers/useScrollPosition';
import { BackToTopButton, PageWrapper } from '../components/base';
import { FilterToggle, LabelFilter } from '../components/search';
import JobsListingItem from '../components/jobs/jobs-listing-item';
import LoadingIndicator from '../components/base/LoadingIndicator';
import ButtonArrow from '../components/buttons/ButtonArrow';
import CrossIcon from '../components/buttons/CrossIcon';
import styles from '../styles/pages/jobs-listing.module.sass';

interface Props {
  doc: {
    page_title: string;
    header: string;
    description: RichTextBlock[];
    no_results: string;
  };
  searchQuery: string;
  sortQuery: string;
  initialJobs: PrismicJobListings;
  initialTotalPages: number;
}

const PAGE_SIZE = 3;
let loadingDelay = null;

const resources = [
  'job-listing.position',
  'job-listing.company',
  'job-listing.city',
  'job-listing.state',
  'job-listing.country',
  'job-listing.description',
  'job-listing.image',
  'job-listing.short_description',
];

const sortOptions = [
  {
    key: 'latest',
    label: 'Latest',
    value: '[document.first_publication_date desc]',
  },
  {
    key: 'position',
    label: 'Job Position',
    value: '[my.job-listing.position]',
  },
  {
    key: 'company',
    label: 'Company',
    value: '[my.job-listing.company]',
  },
];

enum FilterTypes {
  NONE,
  SORT,
}

const CareerBoardPage = ({
  doc,
  searchQuery,
  sortQuery,
  initialJobs,
  initialTotalPages,
}: Props) => {
  const router = useRouter();
  const client = Client();
  const observer = useRef(null);
  const scrollPosition = useScrollPosition();
  const [jobs, setJobs] = useState<any>(initialJobs);
  const [pageNumber, setPageNumber] = useState(2);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [loading, setLoading] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [search, setSearch] = useState(searchQuery);
  const [filterType, setFilterType] = useState(FilterTypes.NONE);
  const [sort, setSort] = useState(sortQuery);

  const lastBusinessRef = useCallback(
    (node) => {
      if (loading) return;

      if (!observer) return;

      observer.current && observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          updatePage();
          // updateJobResults();
        }
      });
      if (node) observer.current.observe(node);
    },
    [jobs]
  );

  useEffect(() => {
    setMinimized(scrollPosition > 100);
  }, [scrollPosition]);

  const updatePage = () => {
    if (pageNumber < totalPages) setPageNumber(pageNumber + 1);
  };

  useEffect(() => {
    if (pageNumber === 0) {
      setPageNumber(1);
      return;
    }
    updateJobResults();

    const selectedSort = _.find(sortOptions, (o) => o.value === sort);
    const query: any = {
      sort: selectedSort.key,
    };
    if (search && search.length) query.q = search;

    router.push(
      {
        pathname: '/jobs-listing',
        query,
      },
      undefined,
      { shallow: true }
    );
  }, [pageNumber]);

  useEffect(() => {
    if (pageNumber === 1) {
      setPageNumber(0);
    } else {
      setPageNumber(1);
    }
  }, [search, sort]);

  const updateJobResults = async () => {
    setLoading(true);

    const filter: any = {
      pageSize: PAGE_SIZE,
      page: pageNumber,
      fetch: resources,
      orderings: sort,
    };

    const query = [Prismic.Predicates.at('document.type', 'job-listing')];

    search &&
      search !== '' &&
      query.push(Prismic.Predicates.fulltext('document', search));

    const jobs = await client.query(query, filter);
    setLoading(false);
    if (jobs) {
      setTotalPages(jobs.total_pages);
      if (jobs.page === 1) {
        setJobs(jobs.results);
      } else {
        setJobs((prevJobs) => [...prevJobs, ...jobs.results]);
      }
    }
  };

  useEffect(() => {
    if (loading) {
      loadingDelay = setTimeout(() => {
        setShowLoading(true);
      }, 200);
    } else {
      if (loadingDelay) {
        setShowLoading(false);
        clearTimeout(loadingDelay);
      }
    }

    return () => {
      if (loadingDelay) clearTimeout(loadingDelay);
    };
  }, [loading]);

  const handleSearchChange = (e) => {
    const value = e.currentTarget.value;
    formik.setFieldValue('search', value);
    if (value === '') setSearch('');
  };

  const handleClearSearch = () => {
    formik.setFieldValue('search', '');
    setSearch('');
  };

  const handleSearchSubmit = (values) => {
    setSearch(values.search);
  };

  const { page_title, header, description, no_results } = doc;

  const formik = useFormik({
    initialValues: {
      search: searchQuery || '',
    },
    onSubmit: handleSearchSubmit,
  });

  return (
    <PageWrapper title={page_title}>
      <section className={`section-first inner-width ${styles.header}`}>
        <div className={styles.inner}>
          <h1 className="sr">{page_title}</h1>
          <h2>{header}</h2>
          <RichText render={description} />
        </div>
      </section>
      <div
        className={`inner-width ${styles.sticky} ${minimized ? styles.collapse : ''
          }`}
      >
        <div className={styles.search}>
          <form className={styles.form} onSubmit={formik.handleSubmit}>
            <section className={styles.inputs}>
              <input
                type="text"
                className={styles.input}
                placeholder="Job Position, Company or Location"
                value={formik.values.search}
                onChange={handleSearchChange}
              />
              {search && search.length > 0 && (
                <button
                  className={`${styles.clear} button`}
                  type="button"
                  onClick={handleClearSearch}
                >
                  <CrossIcon />
                </button>
              )}
              {(!search || search.length === 0) && formik.values.search.length > 0 && (
                <button className={`${styles.submit} button`} type="submit">
                  <ButtonArrow brass />
                </button>
              )}
            </section>
          </form>
          <div className={styles.toggles}>
            <FilterToggle
              label="Sort"
              selected={filterType === FilterTypes.SORT}
              onSelected={() =>
                setFilterType(
                  filterType !== FilterTypes.SORT
                    ? FilterTypes.SORT
                    : FilterTypes.NONE
                )
              }
            />
          </div>
        </div>
        <div className={styles.filters}>
          {filterType === FilterTypes.SORT && (
            <LabelFilter
              data={sortOptions}
              minimized={false}
              selectedFilter={sort}
              onUpdate={(option) => {
                window.scrollTo(0, 0);
                setSort(option);
              }}
            />
          )}
        </div>
      </div>
      <section className={`inner-width inner-width-padding ${styles.gridwrapper}`}>
        <ul className={`full-width ${styles.grid}`}>
          <>
            {jobs && jobs.length > 0 ? (
              jobs.map((job, index) => (
                <li
                  key={`${index}`}
                  ref={index === jobs.length - 1 ? lastBusinessRef : null}
                >
                  <JobsListingItem job={job} />
                </li>
              ))
            ) : (
              <p>{no_results}</p>
            )}
            {showLoading && (
              <div className={styles.loader}>
                <LoadingIndicator inline={true} />
              </div>
            )}
          </>
        </ul>
      </section>
      <BackToTopButton />
    </PageWrapper>
  );
};

export async function getServerSideProps({ query }) {
  const client = Client();
  const doc = await client.getSingle('career-board', {});

  //URL SEARCH + SORT
  const querySort = query.sort
    ? _.find(sortOptions, (o) => o.key === query.sort)
    : null;
  const sortQ = querySort ? querySort.value : sortOptions[0].value;

  const filter: any = {
    pageSize: PAGE_SIZE,
    page: 1,
    fetch: resources,
    orderings: sortQ,
  };

  const prismicQuery = [Prismic.Predicates.at('document.type', 'job-listing')];

  const searchQ = query.q;
  searchQ &&
    searchQ !== '' &&
    prismicQuery.push(Prismic.Predicates.fulltext('document', searchQ));

  const jobs = await client.query(prismicQuery, filter);

  return {
    props: {
      doc: doc.data,
      searchQuery: searchQ ? searchQ : '',
      sortQuery: sortQ,
      initialJobs: jobs.results,
      initialTotalPages: jobs.total_pages,
    },
  };
}

export default CareerBoardPage;
